import useActiveTabIndicator from 'hooks/useActiveTabIndicator';
import PopOnChange from 'components/badge/PopOnChange';

/**
 * Generic pill-style tabs with optional badge count.
 *
 * Usage:
 *   <PillTabs
 *     tabs={[
 *       { id: 'pending', label: 'En attente', count: 12 },
 *       { id: 'done',    label: 'Terminé',    count: 3, disabled: true },
 *     ]}
 *
 * `disabled` marks a tab that cannot be reached yet. It drops the pointer
 * cursor and ignores the click, so it reads as a step you are not on rather
 * than as a control that refuses you — the strip tells you where you are, it
 * is not a set of buttons being withheld.
 *     activeTab="pending"
 *     onChange={(tabId) => setActiveTab(tabId)}
 *   />
 *
 * `variant="counter"` draws the count as the identity validation screen does:
 * orange on the active tab, grey on the others. The default look is unchanged.
 */
const COUNT_CLASSES = {
  default: () => 'bg-yellow-100 text-yellow-800',
  counter: (isActive) =>
    isActive ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-100 text-gray-600',
};

// `wrap`: the tabs go to the next line rather than out of a narrow column.
// `greyZero`: a tab whose count is 0 keeps the grey badge even when active.
const PillTabs = ({
  tabs,
  activeTab,
  onChange,
  variant = 'default',
  wrap = false,
  greyZero = false,
}) => {
  const countClasses = COUNT_CLASSES[variant] || COUNT_CLASSES.default;
  const { listRef, registerTab, indicator } = useActiveTabIndicator(
    activeTab,
    tabs.map((tab) => tab.id),
  );

  return (
    <div ref={listRef} role="tablist" className={`relative flex gap-0.5 ft-sm ${wrap ? 'flex-wrap' : ''}`}>
      {indicator && (
        <span
          data-testid="pill-indicator"
          aria-hidden="true"
          className="pointer-events-none absolute rounded bg-gray-100 motion-safe:transition-[left,width] motion-safe:duration-[250ms] motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={indicator}
        />
      )}
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        // The tab you are on is never "unreachable", whatever the caller says.
        const isDisabled = Boolean(tab.disabled) && !isActive;

        // `relative` lifts the tab above the sliding background, which is
        // positioned and would otherwise paint over the label. 38 px high, as
        // a SM button (components/button) beside the tabs: a bar with one and
        // a bar without are the same height, so switching does not shift.
        return (
          <div
            key={tab.id}
            ref={registerTab(tab.id)}
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled || undefined}
            className={`relative my-auto px-3 py-[9px] rounded transition-colors ${
              isDisabled ? 'cursor-default' : 'cursor-pointer'
            } ${
              isActive
                ? `text-gray-700 ${indicator ? '' : 'bg-gray-100'}`
                : isDisabled
                  ? 'text-gray-500'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
            onClick={isDisabled ? undefined : () => onChange(tab.id)}
          >
            <div className="flex gap-1 items-center">
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span
                  className={`${greyZero && tab.count === 0 ? 'bg-gray-100 text-gray-600' : countClasses(isActive)} rounded px-1 flex items-center justify-center min-w-[20px] text-xs font-medium`}
                >
                  <PopOnChange value={tab.count} />
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PillTabs;
